import { Routes, Route, Navigate } from 'react-router-dom';
import { ProtectedRoute } from './components/ProtectedRoute';
import { CASEWORK } from './components/shell/navigation';
import { Home } from './routes/Home';
import { PublicFileDetail, PublicFiles } from './routes/PublicFiles';
import { Requests } from './routes/Requests';
import { Tickets } from './routes/Tickets';
import { Capture } from './routes/Capture';
import { MapView } from './routes/FieldMap';
import { TeamMap } from './routes/TeamMap';
import { Analytics } from './routes/Analytics';
import { TeamHistory } from './routes/TeamHistory';
import { Dispatch } from './routes/operations/Dispatch';
import { MyDay } from './routes/operations/MyDay';
import { TimeTracking } from './routes/operations/TimeTracking';
import { Invoices } from './routes/operations/Invoices';
import { CostLedger } from './routes/CostLedger';
import { FileCostRecovery } from './routes/FileCostRecovery';
import { Permits } from './routes/permits/Permits';
import { PermitApplicationDetail } from './routes/permits/PermitApplicationDetail';
import { Inspections } from './routes/inspections/Inspections';
import { InspectionDetail } from './routes/inspections/InspectionDetail';
import { SafetyBoard } from './routes/SafetyBoard';
import { FileDetail } from './routes/FileDetail';
import { FileStage } from './routes/FileStage';
import { TombstoneEditor } from './routes/stages/TombstoneEditor';
import { EditFile } from './routes/EditFile';
import { EscalationStep } from './routes/EscalationStep';
import { CourtDocket } from './routes/CourtDocket';
import { NewFile } from './routes/NewFile';
import { BulkImport } from './routes/BulkImport';
import { Triage } from './routes/Triage';
import { Login } from './routes/Login';
import { ForgotPassword } from './routes/ForgotPassword';
import { ResetPassword } from './routes/ResetPassword';
import { Settings } from './routes/Settings';
import { Messages } from './routes/Messages';
import { Inbox } from './routes/Inbox';
import { HandoverDetail, Handovers } from './routes/Handovers';
import { SignoffQueue } from './routes/SignoffQueue';
import { ClerkDashboard } from './routes/ClerkDashboard';
import { InspectionAvailability } from './routes/InspectionAvailability';
import { FrontDesk } from './routes/FrontDesk';
import { MyCallbacks } from './routes/MyCallbacks';
import { VerifyDocument } from './routes/VerifyDocument';
import { Administration } from './routes/administration/Administration';
import { ContractDetail } from './routes/administration/ContractDetail';
import { ContractEdit } from './routes/administration/ContractEdit';

export function App() {
  return (
    <Routes>
      <Route
        path="/messages"
        element={
          <ProtectedRoute>
            <Messages />
          </ProtectedRoute>
        }
      />
      <Route
        path="/inbox"
        element={
          <ProtectedRoute>
            <Inbox />
          </ProtectedRoute>
        }
      />
      <Route
        path="/handovers"
        element={
          <ProtectedRoute>
            <Handovers />
          </ProtectedRoute>
        }
      />
      <Route
        path="/handovers/:id"
        element={
          <ProtectedRoute>
            <HandoverDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/signoffs"
        element={
          <ProtectedRoute>
            <SignoffQueue />
          </ProtectedRoute>
        }
      />
      <Route
        path="/clerk"
        element={
          <ProtectedRoute needsAny={['clerk_dashboard:read']}>
            <ClerkDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/availability"
        element={
          <ProtectedRoute>
            <InspectionAvailability />
          </ProtectedRoute>
        }
      />
      <Route
        path="/front-desk"
        element={
          <ProtectedRoute needsAny={['frontdesk:log']}>
            <FrontDesk />
          </ProtectedRoute>
        }
      />
      <Route
        path="/callbacks"
        element={
          <ProtectedRoute needsAny={['frontdesk:log']}>
            <MyCallbacks />
          </ProtectedRoute>
        }
      />
      {/* Public: whoever holds a signed document can check it, no account. */}
      <Route path="/verify-document" element={<VerifyDocument />} />
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Home />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tickets"
        element={
          <ProtectedRoute needsAny={['ticket:read']}>
            <Tickets />
          </ProtectedRoute>
        }
      />
      <Route
        path="/requests"
        element={
          <ProtectedRoute needsAny={['request:read']}>
            <Requests />
          </ProtectedRoute>
        }
      />
      <Route
        path="/public-files"
        element={
          <ProtectedRoute needsAny={['file:public_read']}>
            <PublicFiles />
          </ProtectedRoute>
        }
      />
      <Route
        path="/public-files/:id"
        element={
          <ProtectedRoute needsAny={['file:public_read']}>
            <PublicFileDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/files/new"
        element={
          // Starting an enforcement file needs an appointment or a municipal
          // seat. Employment alone does not carry it, so a contractor's newly
          // hired officer is sent back to their dashboard rather than into a
          // form the API will refuse to save.
          <ProtectedRoute needsAny={['file:create']}>
            <NewFile />
          </ProtectedRoute>
        }
      />
      {/* Declared before `/files/:id` for readability; React Router ranks by
          specificity so the static segment wins either way. Gated on the same
          permission as single intake — a batch is still creating files. */}
      <Route
        path="/files/import"
        element={
          <ProtectedRoute needsAny={['file:create']}>
            <BulkImport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/files/:id"
        element={
          <ProtectedRoute>
            <FileDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/files/:id/edit"
        element={
          <ProtectedRoute>
            <EditFile />
          </ProtectedRoute>
        }
      />
      {/* Gated by the API on the file: `cost:read` there, and 404 while the
          municipality has cost recovery off. A file seat carries it, so this
          cannot be a scope-level check. */}
      <Route
        path="/files/:id/cost-recovery"
        element={
          <ProtectedRoute>
            <FileCostRecovery />
          </ProtectedRoute>
        }
      />
      <Route
        path="/files/:id/tombstone"
        element={
          <ProtectedRoute>
            <TombstoneEditor />
          </ProtectedRoute>
        }
      />
      <Route
        path="/files/:id/stage/:stage"
        element={
          <ProtectedRoute>
            <FileStage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/files/:id/escalation/:step"
        element={
          <ProtectedRoute>
            <EscalationStep />
          </ProtectedRoute>
        }
      />
      {/* Reading the queue is reading what the municipality has been told about
          properties in it — the same class of information as its files. */}
      <Route
        path="/triage"
        element={
          <ProtectedRoute needsAny={CASEWORK}>
            <Triage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/docket"
        element={
          // The docket lists proceedings on those same files.
          <ProtectedRoute needsAny={CASEWORK}>
            <CourtDocket />
          </ProtectedRoute>
        }
      />
      <Route
        path="/capture"
        element={
          // Capture saves photographs onto a file as evidence.
          <ProtectedRoute needsAny={CASEWORK}>
            <Capture />
          </ProtectedRoute>
        }
      />
      <Route
        path="/map"
        element={
          // The map and the docket both show files. An officer holds
          // `file:create` and reads the ones they are assigned; a clerk holds
          // municipality-wide `file:read`. Either is casework.
          <ProtectedRoute needsAny={CASEWORK}>
            <MapView />
          </ProtectedRoute>
        }
      />
      {/* Supervisors only. The API re-checks tracking:view at the municipality
          on every read and on joining the live room. */}
      <Route
        path="/team"
        element={
          <ProtectedRoute needsAny={['tracking:view']}>
            <TeamMap />
          </ProtectedRoute>
        }
      />
      <Route
        path="/team/history"
        element={
          <ProtectedRoute needsAny={['tracking:view']}>
            <TeamHistory />
          </ProtectedRoute>
        }
      />
      {/* Supervisors only. The API re-checks safety:respond at the
          municipality on every read, write and on joining the live room. */}
      <Route
        path="/safety"
        element={
          <ProtectedRoute needsAny={['safety:respond']}>
            <SafetyBoard />
          </ProtectedRoute>
        }
      />
      {/* Permits & licensing and inspections. The API re-checks the
          permission at the municipality and the plan's module switch on every
          call; the pages say so when the module is off. */}
      <Route
        path="/permits"
        element={
          <ProtectedRoute needsAny={['permit:read']}>
            <Permits />
          </ProtectedRoute>
        }
      />
      <Route
        path="/permits/:tab"
        element={
          <ProtectedRoute needsAny={['permit:read']}>
            <Permits />
          </ProtectedRoute>
        }
      />
      <Route
        path="/permits/applications/:id"
        element={
          <ProtectedRoute needsAny={['permit:read']}>
            <PermitApplicationDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/inspections"
        element={
          <ProtectedRoute needsAny={['inspection:read']}>
            <Inspections />
          </ProtectedRoute>
        }
      />
      <Route
        path="/inspections/view/:tab"
        element={
          <ProtectedRoute needsAny={['inspection:read']}>
            <Inspections />
          </ProtectedRoute>
        }
      />
      <Route
        path="/inspections/:id"
        element={
          <ProtectedRoute needsAny={['inspection:read']}>
            <InspectionDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/cost-recovery"
        element={
          <ProtectedRoute needsAny={['cost:read']}>
            <CostLedger />
          </ProtectedRoute>
        }
      />
      {/* Contractor operations. Offered in the rail only where the module is
          switched on; the API re-checks the permission and the switch on every
          call. */}
      <Route
        path="/operations/dispatch"
        element={
          <ProtectedRoute needsAny={['dispatch:manage']}>
            <Dispatch />
          </ProtectedRoute>
        }
      />
      <Route
        path="/operations/my-day"
        element={
          <ProtectedRoute needsAny={['time:report']}>
            <MyDay />
          </ProtectedRoute>
        }
      />
      <Route
        path="/operations/time"
        element={
          <ProtectedRoute needsAny={['time:report', 'time:approve']}>
            <TimeTracking />
          </ProtectedRoute>
        }
      />
      <Route
        path="/operations/invoices"
        element={
          <ProtectedRoute
            needsAny={['invoice:manage', 'invoice:read']}
            orOperations={(s) => s.companies.length > 0}
          >
            <Invoices />
          </ProtectedRoute>
        }
      />
      {/* Counts and rates only. The API re-checks analytics:read at the
          municipality (or analytics:benchmark for the named comparison). */}
      <Route
        path="/analytics"
        element={
          <ProtectedRoute needsAny={['analytics:read', 'analytics:benchmark']}>
            <Analytics />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        }
      />
      {/* One route with a tab segment rather than a route per tab: which tabs
          exist depends on the caller's permissions in the active scope, so the
          router cannot know them and the section resolves its own default. */}
      <Route
        path="/administration"
        element={
          <ProtectedRoute>
            <Administration />
          </ProtectedRoute>
        }
      />
      {/* Declared before the `:tab` route only for readability — React Router
          ranks by specificity, so these win over it regardless. */}
      <Route
        path="/administration/contracts/:id"
        element={
          <ProtectedRoute>
            <ContractDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/administration/contracts/:id/edit"
        element={
          <ProtectedRoute>
            <ContractEdit />
          </ProtectedRoute>
        }
      />
      <Route
        path="/administration/:tab"
        element={
          <ProtectedRoute>
            <Administration />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
